import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  Button,
  TextField,
  Grid,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Divider
} from '@mui/material';
import { Save, Cancel, ArrowBack, Delete, Edit } from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { teacherAPI } from '../../services/api';
import { useAuth } from '../../context/AuthContext';

const AddSubject = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { user, logout } = useAuth();
  const [loading, setLoading] = useState(false);
  const [classData, setClassData] = useState(null);
  const [subjects, setSubjects] = useState([]);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [formData, setFormData] = useState({
    subject_name: '',
    subject_code: '',
    description: ''
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await teacherAPI.getClasses();
        const classes = response.data.classes || [];
        const currentClass = classes.find(c => c.id === id);
        setClassData(currentClass);
        
        // Fetch subjects for this class
        const subjectsResponse = await teacherAPI.getClassSubjects(id);
        setSubjects(subjectsResponse.data.subjects || []);
      } catch (error) {
        console.error('Error fetching data:', error);
      }
    };
    
    fetchData();
  }, [id]);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      // Create subject via API
      await teacherAPI.createSubject(id, formData);
      
      setSuccess('Subject added successfully!');
      
      // Reset form
      setFormData({
        subject_name: '',
        subject_code: '',
        description: ''
      });

      // Refresh subjects list
      const subjectsResponse = await teacherAPI.getClassSubjects(id);
      setSubjects(subjectsResponse.data.subjects || []);

    } catch (error) {
      setError(error.response?.data?.message || 'Failed to add subject');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (subjectId) => {
    if (window.confirm('Are you sure you want to delete this subject?')) {
      try {
        // Delete subject via API
        await teacherAPI.deleteSubject(subjectId);
        
        // Refresh subjects list
        const subjectsResponse = await teacherAPI.getClassSubjects(id);
        setSubjects(subjectsResponse.data.subjects || []);
        
        setSuccess('Subject deleted successfully');
      } catch (error) {
        setError('Failed to delete subject');
      }
    }
  };

  return (
    <Container maxWidth="lg" sx={{ mt: { xs: 2, md: 4 }, mb: 4, px: { xs: 1.5, sm: 3 } }}>
      <Box display="flex" flexDirection={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'stretch', sm: 'center' }} gap={2} mb={3}>
        <Box>
          <Typography variant="h4" fontWeight="bold" sx={{ fontSize: { xs: '1.5rem', sm: '2.125rem' } }}>
            Manage Subjects
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            {classData?.name} {classData?.grade && `• Grade ${classData.grade}`} {classData?.section && `• Section ${classData.section}`}
          </Typography>
        </Box>
        <Button
          variant="outlined"
          startIcon={<ArrowBack />}
          onClick={() => navigate(`/teacher/classes/${id}/manage`)}
        >
          Back
        </Button>
      </Box>

      {/* Add Subject Form */}
      <Paper sx={{ p: 3, mb: 4 }}>
        <Typography variant="h6" fontWeight="bold" gutterBottom>
          Add New Subject
        </Typography>
        <Divider sx={{ mb: 3 }} />

        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess('')}>
            {success}
          </Alert>
        )}

        <form onSubmit={handleSubmit}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                required
                label="Subject Name"
                name="subject_name"
                value={formData.subject_name}
                onChange={handleChange}
                placeholder="e.g., Mathematics, English"
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                required
                label="Subject Code"
                name="subject_code"
                value={formData.subject_code}
                onChange={handleChange}
                placeholder="e.g., MATH101, ENG101"
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Description"
                name="description"
                value={formData.description}
                onChange={handleChange}
                placeholder="Brief description of the subject"
              />
            </Grid>

            <Grid item xs={12}>
              <Box display="flex" gap={2} justifyContent="flex-end">
                <Button
                  variant="outlined"
                  startIcon={<Cancel />}
                  onClick={() => {
                    setFormData({
                      subject_name: '',
                      subject_code: '',
                      description: ''
                    });
                    setError('');
                    setSuccess('');
                  }}
                  disabled={loading}
                >
                  Clear
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  startIcon={loading ? <CircularProgress size={20} /> : <Save />}
                  disabled={loading}
                >
                  {loading ? 'Adding...' : 'Add Subject'}
                </Button>
              </Box>
            </Grid>
          </Grid>
        </form>
      </Paper>

      {/* Subjects List */}
      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" fontWeight="bold" gutterBottom>
          Existing Subjects
        </Typography>
        <Divider sx={{ mb: 2 }} />

        {subjects.length === 0 ? (
          <Box py={4} textAlign="center">
            <Typography color="text.secondary">
              No subjects added yet. Add your first subject above.
            </Typography>
          </Box>
        ) : (
          <TableContainer component={Paper} sx={{ overflowX: 'auto' }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell><strong>Subject Name</strong></TableCell>
                  <TableCell><strong>Subject Code</strong></TableCell>
                  <TableCell><strong>Description</strong></TableCell>
                  <TableCell align="center"><strong>Actions</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {subjects.map((subject) => (
                  <TableRow key={subject.id} hover>
                    <TableCell>{subject.subject_name}</TableCell>
                    <TableCell>{subject.subject_code}</TableCell>
                    <TableCell>{subject.description || '-'}</TableCell>
                    <TableCell align="center">
                      <IconButton size="small" color="primary" title="Edit">
                        <Edit />
                      </IconButton>
                      <IconButton 
                        size="small" 
                        color="error" 
                        title="Delete"
                        onClick={() => handleDelete(subject.id)}
                      >
                        <Delete />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>
    </Container>
  );
};

export default AddSubject;
